import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Repeat, UserX } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { ContactLinks, fmtDate, gbp } from './shared';

function RateBar({ rate }) {
  if (rate === null || rate === undefined) return <span className="text-xs text-gray-400">No past courses</span>;
  const tone = rate >= 90 ? 'bg-emerald-500' : rate >= 75 ? 'bg-amber-500' : 'bg-rose-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden"><div className={`h-full ${tone}`} style={{ width: `${Math.max(2, rate)}%` }} /></div>
      <span className="text-xs font-bold tabular-nums w-12 text-right">{rate}%</span>
    </div>
  );
}

export function AttendanceSection({ data }) {
  const [showAll, setShowAll] = useState(false);
  const a = data.attendance;
  const t = data.totals;
  const rows = showAll ? a.no_shows : a.no_shows.slice(0, 10);
  return (
    <Card className="p-5" data-testid="attendance-section">
      <div className="flex items-center gap-2 mb-3">
        <UserX className="w-5 h-5 text-rose-500" />
        <h2 className="font-bold text-gray-900">Attendance &amp; no-shows</h2>
        <span className="ml-auto text-xs text-gray-500">Past courses in range · un-ticked bookings count as no-shows · cancelled rows excluded</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-gray-500">Overall</div>
          <div className="text-2xl font-bold text-gray-900" data-testid="attendance-rate">{t.attendance_rate === null ? '—' : `${t.attendance_rate}%`}</div>
          <div className="text-[11px] text-gray-500">{t.attended} attended · <span className="text-rose-700 font-semibold" data-testid="no-show-count">{t.no_shows} no-show{t.no_shows === 1 ? '' : 's'}</span> · {t.cancelled} cancelled</div>
        </div>
        {Object.entries(a.by_brand).map(([k, b]) => (
          <div key={k} data-testid={`attendance-brand-${k}`}>
            <div className="text-xs uppercase tracking-wider text-gray-500">{b.label}</div>
            <RateBar rate={b.rate} />
            <div className="text-[11px] text-gray-500 mt-0.5">{b.attended} attended · {b.no_shows} no-show{b.no_shows === 1 ? '' : 's'}</div>
          </div>
        ))}
      </div>

      {a.repeat_no_shows.length > 0 && (
        <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-900" data-testid="repeat-no-shows">
          <div className="font-bold mb-1 flex items-center gap-1"><Repeat className="w-3.5 h-3.5" /> Repeat no-shows</div>
          <div className="flex flex-wrap gap-2">
            {a.repeat_no_shows.map((r) => <span key={r.registration_id} className="bg-white border border-rose-200 rounded-full px-2 py-0.5">{r.name} · {r.times_no_show}×</span>)}
          </div>
        </div>
      )}

      {a.no_shows.length === 0 ? (
        <p className="text-sm text-gray-500 italic" data-testid="no-shows-empty">No no-shows in this window.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="no-shows-table">
            <thead className="text-[11px] uppercase tracking-wider text-gray-500 border-b">
              <tr><th className="text-left py-1.5 pr-3">Trainee</th><th className="text-left py-1.5 pr-3">Course</th><th className="text-left py-1.5 pr-3">Date</th><th className="text-right py-1.5 pr-3">Owed</th><th className="text-left py-1.5 pr-3">Rebooked?</th><th className="text-left py-1.5">Contact</th></tr>
            </thead>
            <tbody className="divide-y">
              {rows.map((r) => (
                <tr key={r.registration_id} data-testid={`no-show-row-${r.registration_id}`}>
                  <td className="py-1.5 pr-3">
                    <Link to={`/staff/training-courses/${r.course_id}`} className="font-semibold text-gray-900 hover:underline">{r.name}</Link>
                    {r.times_no_show > 1 && <span className="ml-1 text-[10px] font-bold text-rose-700">{r.times_no_show}×</span>}
                  </td>
                  <td className="py-1.5 pr-3 text-gray-700">{r.course} <span className="text-[10px] text-gray-400">{r.brand_label}</span></td>
                  <td className="py-1.5 pr-3 text-gray-600 whitespace-nowrap">{fmtDate(r.date)}</td>
                  <td className={`py-1.5 pr-3 text-right tabular-nums ${r.owed > 0 ? 'text-rose-700 font-semibold' : 'text-gray-500'}`}>{r.owed > 0 ? gbp(r.owed) : 'Paid'}</td>
                  <td className="py-1.5 pr-3 text-xs">{r.rebooked ? <span className="text-emerald-700 font-semibold">Yes · {fmtDate(r.rebooked_date)}</span> : <span className="text-gray-400">No</span>}</td>
                  <td className="py-1.5"><ContactLinks row={r} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {a.no_shows.length > 10 && (
            <button type="button" onClick={() => setShowAll((s) => !s)} className="mt-2 text-xs text-emerald-700 hover:underline" data-testid="no-shows-toggle">
              {showAll ? 'Show fewer' : `Show all ${a.no_shows.length}`}
            </button>
          )}
        </div>
      )}
      <p className="text-[11px] text-gray-500 mt-3">{a.rebooked_count} of {a.no_shows.length} no-show{a.no_shows.length === 1 ? '' : 's'} later rebooked onto another course.</p>
    </Card>
  );
}
